import { getCookie, setCookie } from 'acces-impot-utils/lib/cookie'
import { REFERRAL_URL_PARAM } from 'acces-impot-utils/lib/user'
import { REFERRAL_OUTCOMES } from 'acces-impot-utils/lib/referral'
import { getApiClient } from '@/api/client'
import REFERRER_INFO_QUERY from '@/api/queries/referrer-info.gql'
import LINK_REFERRAL_GQL from '@/api/mutations/link-referral.gql'
import { mergeLocaleMessages } from '@/helpers/i18n'
import { capitalizeFirstLetter } from '@/helpers/string'
import REFERRAL_TRANSLATIONS from '@/locales/referral.json'

export const REFERRAL_COOKIE_KEY = 'referral_code'
export const REFERRAL_COOKIE_EXPIRY_DAYS = 30
const REFERRAL_DISCOUNT_AMOUNT = 5
const WELCOME_MESSAGE_DELAY_MS = 1000

/**
 * Get the referral code from the cookie
 * @param {{ req?: import('http').IncomingMessage }} options
 * @returns {string}
 */
export function getReferralCode(options = {}) {
  return getCookie({ req: options.req, key: REFERRAL_COOKIE_KEY })
}

/**
 * Clear the referral code cookie
 */
export function clearReferralCode() {
  if (typeof document === 'undefined') return

  setCookie({
    key: REFERRAL_COOKIE_KEY,
    value: '',
    daysBeforeExpiration: -1,
  })
}

/**
 * Persist a validated referral code for signup (client-only).
 * @param {string} referralCode
 */
function setReferralCodeCookie(referralCode) {
  if (typeof document === 'undefined') return

  setCookie({
    key: REFERRAL_COOKIE_KEY,
    value: String(referralCode),
    daysBeforeExpiration: REFERRAL_COOKIE_EXPIRY_DAYS,
  })
}

/**
 * @param {import('@nuxt/types').Context['app']} app
 * @returns {boolean}
 */
function isLoggedIn(app) {
  return Boolean(app.$apolloHelpers?.getToken?.())
}

/**
 * Gives Apollo a moment to mount when the referral plugin runs early on navigation.
 * @param {import('@nuxt/types').Context['app']} app
 * @returns {Promise<void>}
 */
async function waitForApolloClient(app) {
  if (getApiClient(app)) return
  await new Promise(resolve => setTimeout(resolve, 200))
}

/**
 * Try to link referral - backend will validate authentication
 * @param {Pick<import('@nuxt/types').Context, 'app'>} context
 * @param {string} referralCode
 * @returns {Promise<boolean>} true when the server accepted the link (`linkReferral.success`)
 */
export async function tryLinkReferral(context, referralCode) {
  const { app } = context

  try {
    const client = getApiClient(app)
    if (!client) return false

    const result = await client.mutate({
      mutation: LINK_REFERRAL_GQL,
      variables: { code: referralCode },
    })

    if (result?.errors?.length) return false

    if (result?.data?.linkReferral?.success) {
      clearReferralCode()
      return true
    }
    return false
  } catch {
    return false
  }
}

/**
 * @param {import('@nuxt/types').Context} context
 * @param {string | null | undefined} referrerFirstName
 */
function showReferralWelcomeMessage(context, referrerFirstName) {
  const { store, app } = context

  const startTime = Date.now()
  const getMessageDelay = () => Math.max(WELCOME_MESSAGE_DELAY_MS - (Date.now() - startTime), 0)

  const trimmedReferrerName = referrerFirstName?.trim()
  const messageText = trimmedReferrerName
    ? app.i18n.t('profile.referral.welcome.withName', {
        name: capitalizeFirstLetter(trimmedReferrerName),
        amount: REFERRAL_DISCOUNT_AMOUNT,
      })
    : app.i18n.t('profile.referral.welcome.generic', {
        amount: REFERRAL_DISCOUNT_AMOUNT,
      })

  setTimeout(() => {
    store.dispatch('flash/addSuccessMessage', {
      key: 'referral-welcome',
      text: messageText,
      isAutoHiding: false,
    })
  }, getMessageDelay())
}

/**
 * @param {import('@nuxt/types').Context} context
 * @param {string} referralCode
 * @returns {Promise<import('acces-impot-utils/lib/referral').ReferrerInfoFetchResult>}
 */
async function fetchReferrerInfo(context, referralCode) {
  const { app } = context
  const client = getApiClient(app)
  if (!client) {
    return { outcome: REFERRAL_OUTCOMES.noClient }
  }

  try {
    const result = await client.query({
      query: REFERRER_INFO_QUERY,
      variables: { code: referralCode },
      fetchPolicy: 'network-only',
    })

    if (result?.errors?.length) {
      return { outcome: REFERRAL_OUTCOMES.error }
    }

    const info = result?.data?.referrerInfo
    if (!info || typeof info.valid !== 'boolean') {
      return { outcome: REFERRAL_OUTCOMES.error }
    }

    return {
      outcome: REFERRAL_OUTCOMES.ok,
      valid: info.valid,
      firstName: info.firstName ?? null,
    }
  } catch {
    return { outcome: REFERRAL_OUTCOMES.error }
  }
}

/**
 * Banner only after the API confirms a decryptable code for an existing referrer.
 * Logged-in: also requires `linkReferral` success (eligibility).
 * @param {import('@nuxt/types').Context} context
 * @param {string} referralCode
 * @returns {Promise<void>}
 */
async function handleReferralTrackingClient(context, referralCode) {
  const { app } = context

  await waitForApolloClient(app)

  const referrerResult = await fetchReferrerInfo(context, referralCode)

  if (referrerResult.outcome === REFERRAL_OUTCOMES.ok && referrerResult.valid === false) {
    return
  }

  if (referrerResult.outcome !== REFERRAL_OUTCOMES.ok || !referrerResult.valid) {
    return
  }

  setReferralCodeCookie(referralCode)

  const firstName = referrerResult.firstName

  if (isLoggedIn(app)) {
    const linked = await tryLinkReferral(context, referralCode)
    if (linked) {
      showReferralWelcomeMessage(context, firstName)
    }
    return
  }

  showReferralWelcomeMessage(context, firstName)
}

/**
 * Extract referral code from URL; cookie is set only after `referrerInfo` reports `valid`.
 * @param {import('@nuxt/types').Context} context
 */
function trackReferralFromUrl(context) {
  const { query, route, app } = context

  const referralCode = query[REFERRAL_URL_PARAM] || route.query[REFERRAL_URL_PARAM]
  if (!referralCode) return

  if (process.client) {
    setTimeout(() => {
      const newQuery = { ...route.query }
      delete newQuery[REFERRAL_URL_PARAM]

      app.router.replace({ path: route.path, query: newQuery }).catch(() => {})
    }, 0)

    void handleReferralTrackingClient(context, String(referralCode))
  }
}

export default context => {
  mergeLocaleMessages(context.app.i18n, REFERRAL_TRANSLATIONS)
  trackReferralFromUrl(context)
}
